import storage from "redux-persist/lib/storage";
import { createTransform } from "redux-persist";

/**
 * Transform to clean up loading and error states before persisting
 * We don't want to persist loading=true or stale errors
 */
const cleanStateTransform = createTransform(
  // Transform state on its way to being serialized and persisted
  (inboundState, key) => {
    // For slices that have creation/update/display structure
    if (inboundState && typeof inboundState === "object") {
      const cleanedState = { ...inboundState };

      // Clean display state - remove loading and error
      if (cleanedState.display) {
        cleanedState.display = {
          ...cleanedState.display,
          loading: false,
          error: null,
        };
      }

      return cleanedState;
    }

    return inboundState;
  },
  // Transform state being rehydrated — restore original snapshot if present
  (outboundState, key) => {
    if (!outboundState) return outboundState;
    const next = { ...outboundState };
    if (next.creation?.originalData) {
      next.creation = {
        ...next.creation,
        ...next.creation.originalData,
        originalData: next.creation.originalData,
      };
    }
    return next;
  },
  // Apply this transform to specific slices
  {
    whitelist: [
      "project",
      "projectObjective",
      "qualificationStep",
      "qualification",
      "page",
    ],
  },
);

/**
 * Redux Persist Configuration
 * - Persists creation and display state to localStorage
 * - Excludes loading and error states from persistence
 * - Uses localStorage as the storage engine
 */
const persistConfig = {
  key: "mercury-admin-redux",
  version: 3, // Incremented to clear old persisted data
  storage,
  // Whitelist the slices we want to persist
  whitelist: [
    "project",
    "projectObjective",
    "qualificationStep",
    "qualification",
    "page",
    "navigation",
  ],
  // Apply transforms to clean state before persisting
  transforms: [cleanStateTransform],
  // Throttle writes to localStorage
  throttle: 1000,
  // Migration function must return a Promise
  migrate: (state) => {
    try {
      // If version doesn't match, resolve with empty state (clears old data)
      if (!state || !state._persist || state._persist.version !== 3) {
        console.log("🔄 Clearing old persisted data due to version mismatch");
        return Promise.resolve({});
      }
      return Promise.resolve(state);
    } catch (e) {
      // On any error, resolve with empty to avoid blocking rehydrate
      console.warn("⚠️ Persist migrate error, resetting state:", e);
      return Promise.resolve({});
    }
  },
};

export default persistConfig;
